import React, { Component, ReactNode, createRef } from 'react';
import {
	DragDisabledItem,
	Platform,
	Orientation,
	DeviceType,
	IModuleSwiperItem,
	GhostPositionHelper,
	WriteDirection,
} from 'sofa-reborn-react';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { StringUtility } from '@inter-ikea-kompis/utilities';
import {
	ItemContainer,
	ImageIconContainer,
	InformationContainer,
	TextContainer,
	StatusWrapper,
	ImageWrapper,
} from './ModuleSwiperItemStyle';
import DragAndDropItem from '@inter-ikea-kompis/component-drag-and-drop/lib/react/DragAndDropItem';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import StatusIndicatorFontSizeEnum from '@inter-ikea-kompis/component-status-indicator/lib/enums/StatusIndicatorFontSizeEnum';
import { localeService } from '../../../services';
import DragDirectionEnum from '@inter-ikea-kompis/component-drag-and-drop/lib/enums/DragDirectionEnum';
import {
	KompisFunctionIcon,
	KompisStatusIndicator,
	KompisText,
	KompisImage,
} from '@inter-ikea-kompis/react-components';

type Props = {
	platform: Platform;
	orientation: Orientation;
	writeDirection: WriteDirection;
	deviceType: DeviceType;
	moduleItem: IModuleSwiperItem;
	id: string;
	isDragged: boolean;
	twoColumns: boolean;
	isFirst: boolean;
	isLast: boolean;
	onItemDragStart?: (itemId: string) => void;
	onGhostPositionUpdate?: (ghost: GhostPositionHelper) => void;
	onItemDragDisabled?: (
		disabledItemId: DragDisabledItem,
		myRef: React.RefObject<HTMLDivElement>
	) => void;
	onItemSelect?: (selectedItemId: IModuleSwiperItem) => void;
};

export default class ModuleSwiperItem extends Component<Props> {
	private myRef = createRef<HTMLDivElement>();

	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	public onDrag = (event): void => {
		const element: HTMLElement = event.target;
		const isDisabled: boolean = element.getAttribute('disabled') === 'true';

		if (isDisabled) {
			const draggedDisabledItem: DragDisabledItem = {
				position: {
					top: 0,
					left: 0,
				},
				size: {
					width: 45,
					height: 25,
				},
			};
			this.props.onItemDragDisabled &&
				this.props.onItemDragDisabled(draggedDisabledItem, this.myRef);
		} else if (!isDisabled) {
			this.props.onItemDragStart && this.props.onItemDragStart(element.id);
		}
	};

	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	public onGhostPositionUpdate = (event): void => {
		const ghost: GhostPositionHelper = {
			imgSrc: this.props.moduleItem.imgSrc,
			x: event.detail.position.x,
			y: event.detail.position.y,
			z: 2,
		};
		this.props.onGhostPositionUpdate && this.props.onGhostPositionUpdate(ghost);
	};

	public handleClick = (element: IModuleSwiperItem): void => {
		const isDisabled: boolean = element.disabled;

		if (isDisabled) {
			const draggedDisabledItem: DragDisabledItem = {
				position: {
					top: 0,
					left: 0,
				},
				size: {
					width: 45,
					height: 25,
				},
			};
			this.props.onItemDragDisabled &&
				this.props.onItemDragDisabled(draggedDisabledItem, this.myRef);
		} else if (!isDisabled) {
			this.props.onItemSelect && this.props.onItemSelect(element);
		}
	};

	public handleTouch = (element: IModuleSwiperItem): void => {
		const isDisabled: boolean = element.disabled;

		if (isDisabled) {
			const draggedDisabledItem: DragDisabledItem = {
				position: {
					top: 0,
					left: 0,
				},
				size: {
					width: 45,
					height: 25,
				},
			};
			this.props.onItemDragDisabled &&
				this.props.onItemDragDisabled(draggedDisabledItem, this.myRef);
		} else if (!isDisabled) {
			this.props.onItemSelect && this.props.onItemSelect(element);
		}
	};

	public renderIcon = () => {
		if (this.props.moduleItem.functionIcon) {
			const showTooltip = this.props.deviceType === 'desktop';
			return (
				<KompisFunctionIcon
					label={showTooltip ? this.props.moduleItem.functionIcon.title : null}
					icon={this.props.moduleItem.functionIcon.icon}
					theme={SkapaTheme}
					// @ts-expect-error Kompis elemenets support style but it isn't exposed
					style={{
						position: 'absolute',
						top: 0,
						right: 0,
						zIndex: 2,
					}}
				/>
			);
		}
		return null;
	};

	public getItemLabel = (): string => {
		let label = localeService
			.getTranslation('moduleSwiperItemLabel')
			.replace('[[sofasection]]', this.props.moduleItem.productName);

		if (this.props.moduleItem.functionIcon?.type === 'storage') {
			label =
				label + ', ' + localeService.getTranslation('sprGalleryFunctionStorage').toLowerCase();
		}

		if (this.props.moduleItem.disabled) {
			label = label + '. ' + localeService.getTranslation('itemCantFitInDesign');
		}

		return label;
	};

	public render(): ReactNode {
		const { props } = this;
		return (
			<DragAndDropItem
				id={props.id}
				disabled={props.moduleItem.disabled}
				onDragItem={(event) => this.onDrag(event)}
				onGhostPositionUpdate={(event) => this.onGhostPositionUpdate(event)}
				dragDirection={
					props.orientation == 'landscape'
						? DragDirectionEnum.vertical
						: DragDirectionEnum.horizontal
				}
				isDragged={props.isDragged}
				style={{
					width: props.twoColumns ? '50%' : 'auto',
					display: props.twoColumns ? 'inline-flex' : 'flex',
					justifyContent: props.twoColumns ? 'center' : 'stretch',
					alignItems: props.twoColumns ? 'center' : 'stretch',
					flexShrink: '0',
					flexBasis: 'auto',
					position: 'relative',
					paddingLeft:
						(props.deviceType === 'mobile' || props.deviceType === 'tablet') &&
						props.orientation === 'portrait' &&
						((props.isFirst && props.writeDirection === 'ltr') ||
							(props.isLast && props.writeDirection === 'rtl'))
							? '40px'
							: '0',
					paddingRight:
						(props.deviceType === 'mobile' || props.deviceType === 'tablet') &&
						props.orientation === 'portrait' &&
						((props.isLast && props.writeDirection === 'ltr') ||
							(props.isFirst && props.writeDirection === 'rtl'))
							? '40px'
							: '0',
					paddingTop: props.platform === 'kiosk' ? '8px' : '0',
					paddingBottom: props.platform === 'kiosk' ? '8px' : '0',
				}}
			>
				<ItemContainer
					id="module-swiper-item"
					disabled={props.moduleItem.disabled}
					onMouseDown={() => this.handleClick(props.moduleItem)}
					onTouchStart={() => this.handleTouch(props.moduleItem)}
					onKeyDown={(e): void => {
						if (e.key === ' ' || e.key === 'Enter') {
							e.preventDefault();
							this.handleClick(props.moduleItem);
						}
					}}
					role="button"
					tabIndex={0}
					aria-label={this.getItemLabel()}
					ref={this.myRef}
				>
					<ImageIconContainer
						isDragged={props.isDragged}
						platform={props.platform}
						deviceType={props.deviceType}
						orientation={props.orientation}
						stockStatus={!!props.moduleItem.stockStatus}
						disabled={props.moduleItem.disabled}
					>
						{this.renderIcon()}
						<ImageWrapper isKiosk={props.platform === 'kiosk'}>
							<KompisImage src={props.moduleItem.imgSrc} alt={props.moduleItem.productName} />
						</ImageWrapper>
					</ImageIconContainer>
					{this.props.deviceType !== 'mobile' && (
						<InformationContainer
							isKiosk={props.platform === 'kiosk'}
							deviceType={props.deviceType}
						>
							<TextContainer
								disabled={props.moduleItem.disabled}
								isKiosk={props.platform === 'kiosk'}
							>
								<KompisText
									theme={SkapaTheme}
									type={
										this.props.deviceType === 'tablet' && this.props.moduleItem.stockStatus
											? ThemeFontStyleTypeEnum.bodySmall
											: ThemeFontStyleTypeEnum.bodyMedium
									}
								>
									{StringUtility.capitalizeFirstLetter(props.moduleItem.productName)}
								</KompisText>
							</TextContainer>
							{this.props.platform === 'kiosk' && this.props.moduleItem.stockStatus && (
								<StatusWrapper disabled={props.moduleItem.disabled}>
									<KompisStatusIndicator
										statusColor={props.moduleItem.stockStatus?.statusColor}
										statusLabel={props.moduleItem.stockStatus?.statusLabel}
										theme={SkapaTheme}
										size={StatusIndicatorFontSizeEnum.small}
									/>
								</StatusWrapper>
							)}
						</InformationContainer>
					)}
				</ItemContainer>
			</DragAndDropItem>
		);
	}
}
